import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AppContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle2, XCircle, Zap } from 'lucide-react';

export default function MagicLink() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | error | expired
  const [errorMsg, setErrorMsg] = useState('');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Lien invalide');
      return;
    }

    (async () => {
      try {
        const res = await api.get(`/auth/magic-link/${token}`);
        const data = res.data;

        // Store tokens
        localStorage.setItem('session_token', data.access_token || data.session_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }

        // Set user in context
        if (data.user) {
          setUser(data.user);
          setUserName(data.user.name || '');
        }

        setStatus('success');

        // Auto-redirect after 2s
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2500);

      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (detail.includes('expire') || err.response?.status === 410) {
          setStatus('expired');
          setErrorMsg('Ce lien a expire ou a deja ete utilise.');
        } else {
          setStatus('error');
          setErrorMsg(detail || 'Erreur d\'activation');
        }
      }
    })();
  }, [token, navigate, setUser]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-border bg-card" data-testid="magic-link-page">
        <CardContent className="p-8 text-center space-y-4">
          {/* Loading */}
          {status === 'loading' && (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight">
                Activation en cours...
              </h2>
              <p className="text-sm text-muted-foreground">
                Configuration de ton profil YUMIX
              </p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto" data-testid="magic-link-success">
                <CheckCircle2 className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-green-400">
                Bienvenue{userName ? `, ${userName}` : ''} !
              </h2>
              <p className="text-sm text-muted-foreground">
                Ton profil est actif. Redirection vers ton tableau de bord...
              </p>
              <div className="flex items-center justify-center gap-2 text-xs text-primary">
                <Zap className="w-3 h-3" /> YUMIX ACTIVE
              </div>
            </>
          )}

          {/* Error / Expired */}
          {(status === 'error' || status === 'expired') && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto" data-testid="magic-link-error">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-red-400">
                {status === 'expired' ? 'Lien Expire' : 'Erreur'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {errorMsg || 'Ce lien d\'activation n\'est plus valide.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/', { replace: true })} className="mt-2" data-testid="magic-link-home-btn">
                Retour a l'accueil
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
